import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import {
  Button,
  ButtonVariant,
  LoadingBar,
  SwitchButton,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import api from "../../../../util/api";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const Section = styled.div`
  border: 1px solid #e5eaf0;
  border-radius: 8px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #fff;
`;

const SectionTitle = styled(Typography)`
  font-weight: 700;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
  align-items: baseline;
`;

const Label = styled(Typography)`
  font-weight: 600;
  color: #495b6c;
  min-width: 160px;
`;

const Value = styled(Typography)`
  color: #333;
  word-break: break-all;
`;

const Badge = styled.span<{ color: string; bg: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => p.color};
  background: ${(p) => p.bg};
`;

const STAGE_TONES: Record<
  string,
  { border: string; bg: string; color: string }
> = {
  green: { border: "#2cd19e", bg: "#e8faf3", color: "#0f6b4d" },
  amber: { border: "#f5c26b", bg: "#fffaf0", color: "#8a5a00" },
  red: { border: "#f19a9a", bg: "#fdecec", color: "#9b1c1c" },
};

const StageBanner = styled.div<{ tone: string }>`
  border: 1px solid ${(p) => STAGE_TONES[p.tone].border};
  background: ${(p) => STAGE_TONES[p.tone].bg};
  border-radius: 10px;
  padding: 14px 18px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  & > :first-child {
    color: ${(p) => STAGE_TONES[p.tone].color};
  }
`;

// Undocumented verification history steps after KYB approval: Niftipay emails the
// merchant a contract and enables card payments once it's signed.
const contractStage = (history?: Array<{ status: string }>) => ({
  sent: !!history?.some((h) => h.status === "contract_sent"),
  signed: !!history?.some((h) =>
    /^contract_(signed|completed|executed|accepted)$/.test(h.status)
  ),
});

const HintText = styled(Typography)`
  color: #8a9bb0;
`;

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DescText = styled(Typography)`
  color: #495b6c;
  line-height: 1.5;
`;

interface Props {
  companyId: string;
}

function statusBadge(status?: string) {
  switch (status) {
    case "approved":
      return (
        <Badge color="#2e7d32" bg="#e8f5e9">
          Approved
        </Badge>
      );
    case "pending":
    case "submitted":
      return (
        <Badge color="#e65100" bg="#fff3e0">
          {status === "pending" ? "Pending" : "Submitted"}
        </Badge>
      );
    case "rejected":
      return (
        <Badge color="#c62828" bg="#ffebee">
          Rejected
        </Badge>
      );
    case "not_started":
    default:
      return (
        <Badge color="#616161" bg="#f5f5f5">
          Not started
        </Badge>
      );
  }
}

const formatPence = (pence: number) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
    pence / 100
  );
const formatDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

const formatAmount = (n: number) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
    n
  );

interface PaymentStats {
  count: number;
  totalAmount: number;
  totalFees: number;
}

const RaiseForm = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-start;
  margin-top: 8px;

  select,
  input,
  textarea {
    font: inherit;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 6px;
  }
  textarea {
    flex: 1 1 320px;
    min-height: 60px;
  }
`;

// Niftipay's document types; omit for a document with no name of its own.
const DOCUMENT_TYPES = [
  "",
  "certificate_of_incorporation",
  "proof_of_address",
  "director_id",
  "bank_statement",
  "processing_statement",
];

export const CompanyNiftipayTab: React.FC<Props> = ({ companyId }) => {
  const [raiseKind, setRaiseKind] = useState("document");
  const [raiseDocType, setRaiseDocType] = useState("");
  const [raiseMessage, setRaiseMessage] = useState("");
  const [raiseTarget, setRaiseTarget] = useState("");
  const [raising, setRaising] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] =
    useState<Awaited<ReturnType<typeof api.getCompanyNiftipayData>>>(null);
  const [stats, setStats] = useState<PaymentStats | null>(null);
  const [togglingCard, setTogglingCard] = useState(false);
  const [savingProvider, setSavingProvider] = useState(false);
  const [currentProvider, setCurrentProvider] = useState<
    "ryft" | "niftipay" | null
  >(null);
  const [liveCustomer, setLiveCustomer] =
    useState<Awaited<ReturnType<typeof api.getNiftipayLiveCustomer>>>(null);
  const [capabilities, setCapabilities] = useState<
    Awaited<ReturnType<typeof api.getNiftipayLiveCapabilities>>
  >([]);
  const [wallets, setWallets] =
    useState<Awaited<ReturnType<typeof api.getNiftipayLiveWallets>>>(null);
  const [withdrawals, setWithdrawals] = useState<
    Awaited<ReturnType<typeof api.getNiftipayLiveWithdrawals>>
  >([]);
  const [payouts, setPayouts] =
    useState<Awaited<ReturnType<typeof api.getNiftipayLivePayouts>>>(null);
  const [verification, setVerification] =
    useState<Awaited<ReturnType<typeof api.getNiftipayLiveVerification>>>(null);
  const [requirements, setRequirements] = useState<
    Awaited<ReturnType<typeof api.getNiftipayLiveRequirements>>
  >([]);
  const [feePreview, setFeePreview] =
    useState<Awaited<ReturnType<typeof api.getNiftipayFeePreview>>>(null);
  const { showNotification } = useNotification();

  const load = useCallback(async () => {
    setLoading(true);
    const [result, statsResult] = await Promise.all([
      api.getCompanyNiftipayData(companyId),
      api.getNiftipayPaymentStats("monthly", companyId).catch(() => null),
    ]);
    setData(result);
    setStats(statsResult);

    if (result?.partner_customer_id) {
      const now = new Date();
      const [cust, caps, wall, wdraw, pays, verif, reqs, fees] =
        await Promise.all([
          api.getNiftipayLiveCustomer(companyId).catch(() => null),
          api.getNiftipayLiveCapabilities(companyId).catch(() => []),
          api.getNiftipayLiveWallets(companyId).catch(() => null),
          api.getNiftipayLiveWithdrawals(companyId).catch(() => []),
          api.getNiftipayLivePayouts(companyId).catch(() => null),
          api.getNiftipayLiveVerification(companyId).catch(() => null),
          api.getNiftipayLiveRequirements(companyId).catch(() => []),
          api
            .getNiftipayFeePreview(
              companyId,
              now.getUTCFullYear(),
              now.getUTCMonth() + 1
            )
            .catch(() => null),
        ]);
      setLiveCustomer(cust);
      setCapabilities(caps);
      setWallets(wall);
      setWithdrawals(wdraw);
      setPayouts(pays);
      setVerification(verif);
      setRequirements(reqs);
      setFeePreview(fees);
    }

    setLoading(false);
  }, [companyId]);

  const handleToggleCard = async (enabled: boolean) => {
    setTogglingCard(true);
    try {
      await api.toggleNiftipayCardPayments(companyId, enabled);
      showNotification(
        enabled
          ? "Niftipay card payments enabled"
          : "Niftipay card payments disabled",
        NotificationTypes.SUCCESS
      );
      // Update in place — reloading the whole tab for one switch is jarring.
      setData((current: any) =>
        current ? { ...current, fiat_card_enabled: enabled } : current
      );
    } catch (e: any) {
      showNotification(
        e?.message || "Failed to toggle",
        NotificationTypes.DANGER
      );
    } finally {
      setTogglingCard(false);
    }
  };

  useEffect(() => {
    void load();
  }, [load]);

  const raiseRequirement = async () => {
    setRaising(true);
    try {
      await api.raiseNiftipayRequirement(companyId, {
        kind: raiseKind,
        message: raiseMessage.trim(),
        ...(raiseKind === "document" && raiseDocType
          ? { document_type: raiseDocType }
          : {}),
        // Niftipay matches an ID requirement to the person by full name.
        ...(raiseTarget.trim() ? { target: raiseTarget.trim() } : {}),
      });
      setRaiseMessage("");
      setRaiseTarget("");
      showNotification(
        "Requirement sent to the merchant",
        NotificationTypes.SUCCESS
      );
      void load();
    } catch (e: any) {
      showNotification(
        e?.message || "Could not raise requirement",
        NotificationTypes.DANGER
      );
    } finally {
      setRaising(false);
    }
  };

  const closeRequirement = async (
    id: string,
    status: "satisfied" | "cancelled"
  ) => {
    try {
      await api.closeNiftipayRequirement(companyId, id, status);
      showNotification(
        status === "satisfied"
          ? "Requirement accepted"
          : "Requirement cancelled",
        NotificationTypes.SUCCESS
      );
      void load();
    } catch (e: any) {
      showNotification(
        e?.message || "Could not update requirement",
        NotificationTypes.DANGER
      );
    }
  };

  const fiatCard = capabilities.find(
    (c) => c.capability === "payments.fiat_card"
  );
  const niftipayCardReady = !!fiatCard?.enabled && !!fiatCard?.available;

  // Where activation stands once KYB is approved: Niftipay's contract, then its
  // payments.fiat_card capability, then our own switch.
  const activation = (() => {
    if (!data || data.verification_status !== "approved") return null;
    const contract = contractStage(verification?.history);
    if (data.fiat_card_enabled && fiatCard && !niftipayCardReady)
      return {
        tone: "red",
        title: "Fena switch is on, but Niftipay hasn't enabled card payments",
        text: "New orders will be refused by Niftipay until it enables payments.fiat_card.",
      };
    if (data.fiat_card_enabled)
      return {
        tone: "green",
        title: "Live — card payments are active",
        text: "Niftipay has enabled card payments for this merchant.",
      };
    if (fiatCard && !fiatCard.available && fiatCard.reason)
      return {
        tone: "red",
        title: "On hold at Niftipay",
        text: `Niftipay reason: ${fiatCard.reason}`,
      };
    if (niftipayCardReady)
      return {
        tone: "amber",
        title: "Niftipay has enabled card payments",
        text: "Set the card fees for this merchant, then turn on the Fena switch below to let them take card payments.",
      };
    if (contract.signed)
      return {
        tone: "amber",
        title: "Contract signed — waiting for Niftipay to enable card payments",
        text: "Nothing to do on our side yet; this updates when Niftipay switches payments.fiat_card on.",
      };
    if (contract.sent)
      return {
        tone: "amber",
        title: "Waiting for the merchant to sign the Niftipay contract",
        text: "Niftipay emailed the contract to the merchant after KYB approval.",
      };
    return {
      tone: "amber",
      title: "Approved — waiting for Niftipay's contract",
      text: "Niftipay sends the merchant a contract after approval; card payments switch on once it's signed.",
    };
  })();

  if (loading) return <LoadingBar loading />;

  if (!data) {
    return (
      <Wrap>
        <Section>
          <SectionTitle variant={TypographyVariant.HEADER3}>
            Niftipay Onboarding
          </SectionTitle>
          <HintText variant={TypographyVariant.BODY4}>
            This company hasn&apos;t started high-risk card payments onboarding
            yet. Its status will appear here once the merchant begins.
          </HintText>
        </Section>
      </Wrap>
    );
  }

  return (
    <Wrap>
      {activation && (
        <StageBanner tone={activation.tone}>
          <Typography variant={TypographyVariant.HEADER3}>
            {activation.title}
          </Typography>
          <DescText variant={TypographyVariant.BODY4}>
            {activation.text}
          </DescText>
        </StageBanner>
      )}

      <Section>
        <SectionTitle variant={TypographyVariant.HEADER3}>
          Niftipay Onboarding
        </SectionTitle>

        <Row>
          <Label variant={TypographyVariant.BODY4}>Verification status:</Label>
          {statusBadge(data.verification_status)}
        </Row>

        <Row>
          <Label variant={TypographyVariant.BODY4}>Partner customer ID:</Label>
          <Value variant={TypographyVariant.BODY3}>
            {data.partner_customer_id || "—"}
          </Value>
        </Row>

        <Row>
          <Label variant={TypographyVariant.BODY4}>Customer ID:</Label>
          <Value variant={TypographyVariant.BODY3}>
            {data.customer_id || "—"}
          </Value>
        </Row>

        <Row>
          <Label variant={TypographyVariant.BODY4}>
            Card payments enabled:
          </Label>
          <Value variant={TypographyVariant.BODY3}>
            {data.fiat_card_enabled ? "Yes" : "No"}
          </Value>
        </Row>

        <Row>
          <Label variant={TypographyVariant.BODY4}>Integration ID:</Label>
          <Value variant={TypographyVariant.BODY3}>
            {data.integration_id || "—"}
          </Value>
        </Row>

        <Row>
          <Label variant={TypographyVariant.BODY4}>Onboarded at:</Label>
          <Value variant={TypographyVariant.BODY3}>
            {data.onboarded_at
              ? new Date(data.onboarded_at).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })
              : "—"}
          </Value>
        </Row>

        {data.rejection_reason && (
          <Row>
            <Label variant={TypographyVariant.BODY4}>Rejection reason:</Label>
            <Value variant={TypographyVariant.BODY3}>
              {data.rejection_reason}
            </Value>
          </Row>
        )}
      </Section>

      {(verification || requirements.length > 0) && (
        <Section>
          <SectionTitle variant={TypographyVariant.HEADER3}>
            Niftipay Review (live)
          </SectionTitle>
          {verification && (
            <>
              <Row>
                <Label variant={TypographyVariant.BODY4}>Case status:</Label>
                <Value variant={TypographyVariant.BODY3}>
                  {verification.status}
                </Value>
              </Row>
              <Row>
                <Label variant={TypographyVariant.BODY4}>Submitted:</Label>
                <Value variant={TypographyVariant.BODY3}>
                  {formatDate(verification.submitted_at)}
                </Value>
              </Row>
              <Row>
                <Label variant={TypographyVariant.BODY4}>Verified:</Label>
                <Value variant={TypographyVariant.BODY3}>
                  {formatDate(verification.verified_at)}
                </Value>
              </Row>
            </>
          )}
          <DescText variant={TypographyVariant.BODY4}>
            {requirements.length
              ? `Open requirements (${requirements.length}) — the merchant sees these under “Action needed” and was emailed when each was raised.`
              : "No open requirements."}
          </DescText>
          {requirements.map((r) => (
            <Row key={r.requirement_id}>
              <Label variant={TypographyVariant.BODY4}>
                {r.kind} · {r.target} · raised by {r.raised_by}
              </Label>
              <Value variant={TypographyVariant.BODY3}>
                {r.message} —{" "}
                {r.status === "fulfilled" ? "sent, awaiting review" : "open"}
              </Value>
              {/* Only the raiser may accept or withdraw a requirement. */}
              {r.raised_by === "you" && (
                <>
                  {r.status === "fulfilled" && (
                    <Button
                      variant={ButtonVariant.OUTLINED}
                      onClick={() =>
                        closeRequirement(r.requirement_id, "satisfied")
                      }
                    >
                      Accept
                    </Button>
                  )}
                  <Button
                    variant={ButtonVariant.OUTLINED}
                    onClick={() =>
                      closeRequirement(r.requirement_id, "cancelled")
                    }
                  >
                    Cancel
                  </Button>
                </>
              )}
            </Row>
          ))}
          {verification && verification.status !== "approved" && (
            <RaiseForm>
              <select
                aria-label="Requirement type"
                value={raiseKind}
                onChange={(e) => setRaiseKind(e.target.value)}
              >
                <option value="document">Document</option>
                <option value="field">Field correction</option>
                <option value="clarification">Clarification</option>
              </select>
              {raiseKind === "document" && (
                <select
                  aria-label="Document type"
                  value={raiseDocType}
                  onChange={(e) => setRaiseDocType(e.target.value)}
                >
                  {DOCUMENT_TYPES.map((d) => (
                    <option key={d} value={d}>
                      {d ? d.replace(/_/g, " ") : "Other document"}
                    </option>
                  ))}
                </select>
              )}
              {raiseKind === "document" && raiseDocType === "director_id" && (
                <>
                  <input
                    aria-label="Person"
                    list="niftipay-people"
                    placeholder="Director or UBO full name"
                    value={raiseTarget}
                    onChange={(e) => setRaiseTarget(e.target.value)}
                  />
                  <datalist id="niftipay-people">
                    {[
                      ...(verification.directors ?? []),
                      ...(verification.ubos ?? []),
                    ]
                      .map((p) => p.full_name)
                      .filter(Boolean)
                      .map((name) => (
                        <option key={name} value={name} />
                      ))}
                  </datalist>
                </>
              )}
              <textarea
                aria-label="Message to the merchant"
                placeholder="What the merchant should provide (shown to them, max 500 characters)"
                maxLength={500}
                value={raiseMessage}
                onChange={(e) => setRaiseMessage(e.target.value)}
              />
              <Button
                variant={ButtonVariant.CONTAINED}
                onClick={raiseRequirement}
                disabled={raising || !raiseMessage.trim()}
              >
                {raising ? "Sending..." : "Request from merchant"}
              </Button>
            </RaiseForm>
          )}
        </Section>
      )}

      {data.verification_status === "approved" && (
        <Section>
          <SectionTitle variant={TypographyVariant.HEADER3}>
            Card Payments Control
          </SectionTitle>
          <DescText variant={TypographyVariant.BODY4}>
            Use this switch to allow or block Niftipay card payments for this
            merchant. Disabling stops new fiat orders; verification status is
            unchanged.
          </DescText>
          <SwitchRow>
            <SwitchButton
              checked={!!data.fiat_card_enabled}
              onChange={(val: boolean) => void handleToggleCard(val)}
              // Turning it on before Niftipay enables card payments only makes orders fail.
              disabled={
                togglingCard || (!data.fiat_card_enabled && !niftipayCardReady)
              }
            />
            <Typography variant={TypographyVariant.BODY3}>
              {data.fiat_card_enabled
                ? "Card payments enabled"
                : "Card payments disabled (Fena gate)"}
            </Typography>
          </SwitchRow>
          {!data.fiat_card_enabled && !niftipayCardReady && (
            <HintText variant={TypographyVariant.BODY4}>
              Available once Niftipay enables card payments for this merchant.
            </HintText>
          )}
        </Section>
      )}

      {stats && (
        <Section>
          <SectionTitle variant={TypographyVariant.HEADER3}>
            Payment Stats (Last 30 days)
          </SectionTitle>
          <Row>
            <Label variant={TypographyVariant.BODY4}>Completed payments:</Label>
            <Value variant={TypographyVariant.BODY3}>{stats.count}</Value>
          </Row>
          <Row>
            <Label variant={TypographyVariant.BODY4}>Total volume:</Label>
            <Value variant={TypographyVariant.BODY3}>
              {formatAmount(stats.totalAmount)}
            </Value>
          </Row>
          <Row>
            <Label variant={TypographyVariant.BODY4}>Service fees:</Label>
            <Value variant={TypographyVariant.BODY3}>
              {formatAmount(stats.totalFees)}
            </Value>
          </Row>
        </Section>
      )}

      {feePreview && (
        <Section>
          <SectionTitle variant={TypographyVariant.HEADER3}>
            Fees This Month (preview)
          </SectionTitle>
          <Row>
            <Label variant={TypographyVariant.BODY4}>Completed payments:</Label>
            <Value variant={TypographyVariant.BODY3}>
              {feePreview.paymentCount}
            </Value>
          </Row>
          <Row>
            <Label variant={TypographyVariant.BODY4}>Gross paid:</Label>
            <Value variant={TypographyVariant.BODY3}>
              {formatPence(feePreview.grossPaidPence)}
            </Value>
          </Row>
          <Row>
            <Label variant={TypographyVariant.BODY4}>
              Fena administrative fee:
            </Label>
            <Value variant={TypographyVariant.BODY3}>
              {formatPence(feePreview.adminFeePence)}
            </Value>
          </Row>
          <Row>
            <Label variant={TypographyVariant.BODY4}>
              Niftipay service fee:
            </Label>
            <Value variant={TypographyVariant.BODY3}>
              {formatPence(feePreview.processingFeePence)}
            </Value>
          </Row>
          <DescText variant={TypographyVariant.BODY4}>
            The Niftipay service fee is charged by Niftipay (paid by the buyer
            when the merchant chose “customer pays”), not Fena revenue. The Fena
            administrative fee is not collected automatically yet.
            {feePreview.missingAdminFeeCount > 0 &&
              ` ${feePreview.missingAdminFeeCount} payment(s) have no fee snapshot and are excluded.`}
          </DescText>
        </Section>
      )}

      {liveCustomer && (
        <Section>
          <SectionTitle variant={TypographyVariant.HEADER3}>
            Live Customer Detail
          </SectionTitle>
          <Row>
            <Label variant={TypographyVariant.BODY4}>
              Relationship status:
            </Label>
            <Badge
              color={
                liveCustomer.relationship_status === "active"
                  ? "#2e7d32"
                  : "#c62828"
              }
              bg={
                liveCustomer.relationship_status === "active"
                  ? "#e8f5e9"
                  : "#ffebee"
              }
            >
              {liveCustomer.relationship_status}
            </Badge>
          </Row>
          <Row>
            <Label variant={TypographyVariant.BODY4}>Email:</Label>
            <Value variant={TypographyVariant.BODY3}>
              {liveCustomer.customer?.email || "—"}
            </Value>
          </Row>
          <Row>
            <Label variant={TypographyVariant.BODY4}>Name:</Label>
            <Value variant={TypographyVariant.BODY3}>
              {liveCustomer.customer?.name || "—"}
            </Value>
          </Row>
          <Row>
            <Label variant={TypographyVariant.BODY4}>Country:</Label>
            <Value variant={TypographyVariant.BODY3}>
              {liveCustomer.customer?.country || "—"}
            </Value>
          </Row>
          <Row>
            <Label variant={TypographyVariant.BODY4}>Created at:</Label>
            <Value variant={TypographyVariant.BODY3}>
              {new Date(liveCustomer.created_at).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </Value>
          </Row>
        </Section>
      )}

      {capabilities.length > 0 && (
        <Section>
          <SectionTitle variant={TypographyVariant.HEADER3}>
            Capabilities
          </SectionTitle>
          {capabilities.map((cap) => {
            // Usable only when granted (enabled) and not currently blocked (available).
            const usable = cap.enabled && cap.available;
            return (
              <Row key={cap.capability}>
                <Label variant={TypographyVariant.BODY4}>
                  {cap.capability}:
                </Label>
                <Badge
                  color={
                    usable ? "#2e7d32" : cap.enabled ? "#e65100" : "#616161"
                  }
                  bg={usable ? "#e8f5e9" : cap.enabled ? "#fff3e0" : "#f5f5f5"}
                >
                  {usable
                    ? "Active"
                    : cap.enabled
                    ? `Unavailable${cap.reason ? ` (${cap.reason})` : ""}`
                    : "Not enabled"}
                </Badge>
              </Row>
            );
          })}
        </Section>
      )}

      {wallets && wallets.wallets.length > 0 && (
        <Section>
          <SectionTitle variant={TypographyVariant.HEADER3}>
            Wallets &amp; Balances
          </SectionTitle>
          {wallets.wallets.map((w) => (
            <div
              key={w.address}
              style={{
                borderBottom: "1px solid #f0f0f0",
                paddingBottom: 10,
                marginBottom: 10,
              }}
            >
              <Row>
                <Label variant={TypographyVariant.BODY4}>Chain:</Label>
                <Value variant={TypographyVariant.BODY3}>{w.chain}</Value>
              </Row>
              <Row>
                <Label variant={TypographyVariant.BODY4}>Address:</Label>
                <Value
                  variant={TypographyVariant.BODY3}
                  style={{ fontSize: 12 }}
                >
                  {w.address}
                </Value>
              </Row>
              <Row>
                <Label variant={TypographyVariant.BODY4}>Balance:</Label>
                <Value variant={TypographyVariant.BODY3}>
                  {w.balance} {w.chain}
                </Value>
              </Row>
              {w.tokens &&
                Object.entries(w.tokens).map(([token, bal]) => (
                  <Row key={token}>
                    <Label variant={TypographyVariant.BODY4}>{token}:</Label>
                    <Value variant={TypographyVariant.BODY3}>{bal}</Value>
                  </Row>
                ))}
            </div>
          ))}
        </Section>
      )}

      {withdrawals.length > 0 && (
        <Section>
          <SectionTitle variant={TypographyVariant.HEADER3}>
            Withdrawal History
          </SectionTitle>
          {withdrawals.map((w) => (
            <div
              key={w.id}
              style={{
                borderBottom: "1px solid #f0f0f0",
                paddingBottom: 10,
                marginBottom: 10,
              }}
            >
              <Row>
                <Label variant={TypographyVariant.BODY4}>Date:</Label>
                <Value variant={TypographyVariant.BODY3}>
                  {new Date(w.created_at).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </Value>
              </Row>
              <Row>
                <Label variant={TypographyVariant.BODY4}>Asset:</Label>
                <Value variant={TypographyVariant.BODY3}>
                  {w.asset} ({w.chain})
                </Value>
              </Row>
              <Row>
                <Label variant={TypographyVariant.BODY4}>Amount:</Label>
                <Value variant={TypographyVariant.BODY3}>
                  {w.gross} (fee: {w.fee})
                </Value>
              </Row>
              <Row>
                <Label variant={TypographyVariant.BODY4}>Status:</Label>
                <Badge
                  color={w.status === "completed" ? "#2e7d32" : "#e65100"}
                  bg={w.status === "completed" ? "#e8f5e9" : "#fff3e0"}
                >
                  {w.status}
                </Badge>
              </Row>
              {w.tx_hash && (
                <Row>
                  <Label variant={TypographyVariant.BODY4}>TX hash:</Label>
                  <Value
                    variant={TypographyVariant.BODY3}
                    style={{ fontSize: 12 }}
                  >
                    {w.tx_hash}
                  </Value>
                </Row>
              )}
            </div>
          ))}
        </Section>
      )}

      {payouts && payouts.payouts.length > 0 && (
        <Section>
          <SectionTitle variant={TypographyVariant.HEADER3}>
            Payouts / Settlements
          </SectionTitle>
          {payouts.payouts.map((p) => (
            <div
              key={p.id}
              style={{
                borderBottom: "1px solid #f0f0f0",
                paddingBottom: 10,
                marginBottom: 10,
              }}
            >
              <Row>
                <Label variant={TypographyVariant.BODY4}>Due:</Label>
                <Value variant={TypographyVariant.BODY3}>
                  {new Date(p.payout_due_at).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </Value>
              </Row>
              <Row>
                <Label variant={TypographyVariant.BODY4}>Amount:</Label>
                <Value variant={TypographyVariant.BODY3}>
                  {formatAmount(p.net_cents / 100)} {p.currency}
                </Value>
              </Row>
              <Row>
                <Label variant={TypographyVariant.BODY4}>Status:</Label>
                <Badge
                  color={
                    p.status === "paid"
                      ? "#2e7d32"
                      : p.status === "upcoming"
                      ? "#e65100"
                      : "#616161"
                  }
                  bg={
                    p.status === "paid"
                      ? "#e8f5e9"
                      : p.status === "upcoming"
                      ? "#fff3e0"
                      : "#f5f5f5"
                  }
                >
                  {p.status}
                </Badge>
              </Row>
              {p.paid_at && (
                <Row>
                  <Label variant={TypographyVariant.BODY4}>Paid at:</Label>
                  <Value variant={TypographyVariant.BODY3}>
                    {new Date(p.paid_at).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </Value>
                </Row>
              )}
              <Row>
                <Label variant={TypographyVariant.BODY4}>Orders:</Label>
                <Value variant={TypographyVariant.BODY3}>
                  {p.orders_count}
                </Value>
              </Row>
            </div>
          ))}
        </Section>
      )}
    </Wrap>
  );
};
